/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  IEvidence,
  EvidencePart,
  IUser,
  LearningObjective,
  ProgramData,
  EvidenceAttachment,
} from "@interfaces/Interfaces";
import EvidencePartsContent from "./EvidencePartsContent";
import StandardEvidenceContent from "./StandardEvidenceContent";
import LegacyEvidenceFields from "./LegacyEvidenceFields";
import { useLegacyEvidenceRenderer } from "./LegacyEvidenceRenderer";
import ResendEvidenceButton from "./ResendEvidenceButton";
import UOCEvidenceStatusBlock from "./UOCEvidenceStatusBlock";
import * as ProgramUtils from "@utils/programUtils";

interface EvidenceContentProps {
  evidence: IEvidence | null;
  evidenceJSON: Record<string, any> | null;
  evidenceParts: EvidencePart[];
  legacyParts: string[];
  user: IUser;
  program: ProgramData | null;
  viewingAttachment: boolean;
  viewingAttachmentFilename: string;
  onViewAttachment: (attachment: EvidenceAttachment) => void;
  getLearningOutcomesForEvidencePart: () => LearningObjective[];
  onResend: () => Promise<void>;
  isCoolingDown: boolean;
  remindedAt: Date | null;
}

const EvidenceContent: React.FC<EvidenceContentProps> = ({
  evidence,
  evidenceJSON,
  evidenceParts,
  legacyParts,
  user,
  program,
  viewingAttachment,
  viewingAttachmentFilename,
  onViewAttachment,
  getLearningOutcomesForEvidencePart,
  onResend,
  isCoolingDown,
  remindedAt,
}) => {
  const isUoC = ProgramUtils.isUOCApprenticeship(program?.ID ?? "");
  const expiryDate = evidenceJSON?.programInfo?.expiryDate;
  const isPending =
    (evidenceJSON?.programInfo?.pending === true || evidenceJSON?.programInfo?.approvalState === "Pending") &&
    !!expiryDate;
  const isExpired = isPending && new Date(expiryDate) < new Date();

  const { getLegacyEvidencePartType, renderLegacyEvidencePart } = useLegacyEvidenceRenderer({
    viewingAttachment,
    viewingAttachmentFilename,
    onViewAttachment,
  });

  if (!evidence) {
    return (
      <>
        <div className="readEvidenceTitle">{"This piece of evidence doesn't exist"}</div>
        <div className="readEvidenceBlock">
          <div className="readEvidenceText">
            {
              "You may have deleted this piece of evidence recently, try going back and selecting another piece of evidence."
            }
            <br />
            <br />
            {"If you think this is a mistake, please "}
            <a
              className="text-cta-blue underline"
              href="contactSupport"
            >
              {"contact support"}
            </a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {isUoC ? (
        <UOCEvidenceStatusBlock
          evidence={evidence}
          onResend={onResend}
          isCoolingDown={isCoolingDown}
          remindedAt={remindedAt}
        />
      ) : (
        isPending && expiryDate && (
          <ResendEvidenceButton
            onResend={onResend}
            programID={program?.ID}
            isExpired={isExpired}
            expiryDate={expiryDate}
          />
        )
      )}
      {evidenceParts.length > 0 && evidenceJSON && program ? (
        <EvidencePartsContent
          evidence={evidence}
          evidenceJSON={evidenceJSON}
          evidenceParts={evidenceParts}
          user={user}
          program={program}
          getLearningOutcomesForEvidencePart={getLearningOutcomesForEvidencePart}
        />
      ) : (
        <StandardEvidenceContent
          evidence={evidence}
          viewingAttachment={viewingAttachment}
          viewingAttachmentFilename={viewingAttachmentFilename}
          onViewAttachment={onViewAttachment}
        />
      )}

      {evidenceJSON && (
        <LegacyEvidenceFields
          legacyParts={legacyParts}
          evidenceJSON={evidenceJSON}
          getLegacyEvidencePartType={getLegacyEvidencePartType}
          renderLegacyEvidencePart={renderLegacyEvidencePart}
        />
      )}
    </>
  );
};

export default EvidenceContent;
